"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { useCountry } from "@/components/layout/country-provider"
import { TestimonialCard } from "@/components/ui/testimonial-card"
import { VideoTestimonial } from "@/components/ui/video-testimonial"
import { InstagramTestimonial } from "@/components/ui/instagram-testimonial"
import { homeKeys as k } from "@/i18n/home-keys"
import { CarouselNav } from "@/components/ui/carousel-nav"
import Link from "next/link"
import { buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { track } from "@/lib/analytics"
import { cmsMediaUrl, cmsLocale, type Testimonial } from "@/lib/cms"

/** Snap the carousel to the next/previous card boundary. Measures each
 *  child's actual left offset so mixed widths (55vw videos + 80vw reviews)
 *  all land flush. Returns the destination card's 1-based index and the
 *  total card count so callers can announce position to assistive tech. */
function scrollToCard(
  el: HTMLDivElement | null,
  dir: "left" | "right"
): { index: number; total: number } | null {
  if (!el) return null
  const cards = Array.from(el.children) as HTMLElement[]
  if (cards.length === 0) return null
  const containerRect = el.getBoundingClientRect()
  const padLeft = parseFloat(getComputedStyle(el).paddingLeft) || 0
  const current = el.scrollLeft
  const targets = cards.map((c) => {
    const r = c.getBoundingClientRect()
    return Math.max(0, r.left - containerRect.left + current - padLeft)
  })
  // Small nudge past the exact boundary so a sliver of the previous card
  // peeks in from the left after snap — feels more natural than landing
  // flush at the edge.
  const NUDGE = 4
  let nextIdx: number | undefined
  let next: number | undefined
  if (dir === "right") {
    nextIdx = targets.findIndex((t) => t > current + 5)
    if (nextIdx >= 0) {
      next = targets[nextIdx] + NUDGE
    }
  } else {
    for (let i = targets.length - 1; i >= 0; i--) {
      if (targets[i] < current - 5) {
        nextIdx = i
        next = targets[i]
        break
      }
    }
    if (nextIdx === undefined) {
      nextIdx = 0
      next = 0
    } else {
      next = Math.max(0, (next ?? 0) - NUDGE)
    }
  }
  if (next === undefined || nextIdx === undefined || nextIdx < 0) return null
  el.scrollTo({ left: next, behavior: "smooth" })
  return { index: nextIdx + 1, total: cards.length }
}

interface TestimonialsSectionProps {
  /** Optional heading override (default: home.testimonialsHeading). */
  heading?: string
  /**
   * Optional description override. Pass `null` to hide the description.
   * Default: home.testimonialsDescription.
   */
  description?: string | null
  /** Optional uppercase eyebrow rendered above the heading. */
  eyebrow?: string
  /** Override the "view all reviews" destination. Defaults to the localized
   *  `/reviews` page — the button renders on every carousel unless `false`. */
  viewAllHref?: string | false
  /** Override the button label. Defaults to `ui.viewAllReviews`. */
  viewAllLabel?: string
  /** Vertical padding on the section wrapper. Default `py-16 md:py-24`. */
  paddingClass?: string
  /** Heading className override. Default `type-display-md text-center text-neutral-900`. */
  headingClassName?: string
  /**
   * CMS-sourced testimonials — the sole data source. Wire via the server
   * wrapper in `lib/cms-server.ts` (`getCMSTestimonialsServer`). When empty
   * (collection unreachable / no docs for the surface) the section renders
   * nothing rather than an empty rail.
   */
  cmsTestimonials?: Testimonial[]
  /** Aspect-ratio utility for the video tiles (default portrait `aspect-[9/16]`).
   *  Pass a shorter ratio (e.g. `aspect-[4/5]`) to crop the rail height — text
   *  cards `self-stretch` to match, so this shrinks the whole carousel. */
  videoAspectClass?: string
}

/**
 * Shared testimonials carousel — heading + description on top, then an
 * edge-to-edge horizontal scroller mapping each CMS testimonial doc to the
 * matching card component by `type` (text → TestimonialCard, video →
 * VideoTestimonial, instagram-video → InstagramTestimonial), then navigation
 * chevrons. Docs missing required media URLs are dropped before render so a
 * half-filled CMS row never blanks the rail.
 *
 * Uses a ref (not DOM ids) so multiple instances can't collide, and
 * intercepts vertical-dominant wheel events so macOS trackpads don't "grab"
 * the carousel while the user is trying to scroll the page.
 *
 * Scroll-hijacking root cause (AHD-5383): `overflow-x: auto` with the default
 * `overflow-y: visible` computes to `overflow-y: auto` (CSS spec quirk), which
 * makes the flex row a vertical scroll container and lets the browser route
 * wheel events into it. `overflow-y-clip` explicitly keeps it from becoming a
 * scroll container; the JS wheel handler remains as a safety net for any
 * legacy browser that still tries to hijack.
 */
export function TestimonialsSection({
  heading,
  description,
  eyebrow,
  viewAllHref,
  viewAllLabel,
  paddingClass = "py-16 md:py-24",
  headingClassName = "type-display-md text-center text-neutral-900",
  cmsTestimonials,
  videoAspectClass,
}: TestimonialsSectionProps = {}) {
  const t = useTranslations("home")
  const ut = useTranslations("ui")
  const lp = useLocalePath()
  // Default the "view all reviews" button onto every carousel; a caller can
  // point it elsewhere or pass `false` to hide it entirely.
  const resolvedViewAllHref =
    viewAllHref === false ? null : (viewAllHref ?? lp("/reviews"))
  const resolvedViewAllLabel = viewAllLabel ?? ut("viewAllReviews")
  // CMS locale these docs were fetched in — the inline editor stamps it so a
  // draft write targets the matching Payload locale row. No-op in public prod.
  const locale = useLocale()
  const editLocale = cmsLocale(locale, useCountry().code)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(false)
  const [announcement, setAnnouncement] = useState("")

  function navigate(dir: "left" | "right") {
    const result = scrollToCard(scrollRef.current, dir)
    if (!result) return
    setAnnouncement(
      ut("carouselPosition", {
        current: String(result.index),
        total: String(result.total),
      })
    )
  }

  // Track whether the carousel is at either edge so we can disable the
  // matching nav button. Pixel slack avoids flicker from sub-pixel scroll
  // positions after a snap.
  const updateEdges = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setAtStart(el.scrollLeft <= 1)
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    updateEdges()
    el.addEventListener("scroll", updateEdges, { passive: true })
    const ro = new ResizeObserver(updateEdges)
    ro.observe(el)
    return () => {
      el.removeEventListener("scroll", updateEdges)
      ro.disconnect()
    }
  }, [updateEdges])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault()
        window.scrollBy(0, e.deltaY)
      }
    }
    el.addEventListener("wheel", onWheel, { passive: false })

    // Click-and-drag scrolling for mouse users. We track pointer movement
    // beyond a 4px threshold before treating it as a drag — that lets a
    // simple click on a card (open external review URL, play video) still
    // fire normally. After a real drag we swallow the trailing click in
    // capture phase so it doesn't activate a card we just dragged past.
    let dragging = false
    let dragMoved = false
    let startX = 0
    let startScroll = 0
    const DRAG_THRESHOLD = 4
    const onDown = (e: MouseEvent) => {
      if (e.button !== 0) return
      dragging = true
      dragMoved = false
      startX = e.pageX
      startScroll = el.scrollLeft
      el.style.cursor = "grabbing"
    }
    const onMove = (e: MouseEvent) => {
      if (!dragging) return
      const dx = e.pageX - startX
      if (!dragMoved && Math.abs(dx) > DRAG_THRESHOLD) dragMoved = true
      if (dragMoved) {
        e.preventDefault()
        el.scrollLeft = startScroll - dx
      }
    }
    const onUp = () => {
      dragging = false
      el.style.cursor = ""
    }
    const onClickCapture = (e: MouseEvent) => {
      if (dragMoved) {
        e.preventDefault()
        e.stopPropagation()
        dragMoved = false
      }
    }
    el.addEventListener("mousedown", onDown)
    window.addEventListener("mousemove", onMove)
    window.addEventListener("mouseup", onUp)
    el.addEventListener("click", onClickCapture, true)

    return () => {
      el.removeEventListener("wheel", onWheel)
      el.removeEventListener("mousedown", onDown)
      window.removeEventListener("mousemove", onMove)
      window.removeEventListener("mouseup", onUp)
      el.removeEventListener("click", onClickCapture, true)
    }
  }, [])

  const testimonials = cmsTestimonials ?? []
  const resolvedHeading = heading ?? t(k.testimonialsHeading)
  const resolvedDescription =
    description === undefined ? t(k.testimonialsDescription) : description

  // No CMS docs for this surface/locale → render nothing rather than an
  // empty heading + bare rail.
  if (testimonials.length === 0) return null

  return (
    <section className={`lazy-section ${paddingClass}`}>
      <div className="px-4 md:px-10">
        {eyebrow ? (
          <p className="type-label-md mb-2 text-center text-neutral-500">
            {eyebrow}
          </p>
        ) : null}
        <h2 className={headingClassName}>{resolvedHeading}</h2>
        {resolvedDescription ? (
          <p className="type-body-lg mx-auto mt-4 max-w-2xl text-center text-neutral-600">
            {resolvedDescription}
          </p>
        ) : null}
      </div>

      <div
        ref={scrollRef}
        className="hide-scrollbar mt-12 flex cursor-grab gap-3 overflow-x-auto overflow-y-clip overscroll-x-contain pr-4 pb-4 pl-4 select-none active:cursor-grabbing md:gap-4 md:pr-10 md:pl-[max(2.5rem,calc((100vw-1440px)/2+2.5rem))]"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          // Allow both horizontal carousel pan AND vertical page scroll —
          // `pan-x` alone swallows vertical swipes on the videos.
          touchAction: "pan-x pan-y",
        }}
      >
        {testimonials.map((item) => {
          if (item.type === "text") {
            return (
              <div
                key={item.id}
                className="w-[280px] flex-shrink-0 self-stretch"
              >
                <TestimonialCard
                  name={item.authorName}
                  title={item.headline ?? ""}
                  quote={item.quote ?? ""}
                  cmsId={item.id}
                  editLocale={editLocale}
                  rating={item.rating}
                  avatarSrc={
                    item.avatar?.url ? cmsMediaUrl(item.avatar.url) : undefined
                  }
                  externalUrl={item.externalUrl}
                  className="h-full"
                />
              </div>
            )
          }
          if (item.type === "instagram-video") {
            const previewUrl = item.previewVideo?.url
            const fullUrl = item.fullVideo?.url
            const posterUrl = item.posterImage?.url
            if (!previewUrl || !fullUrl || !posterUrl) return null
            return (
              <div key={item.id} className="w-[280px] flex-shrink-0">
                <InstagramTestimonial
                  previewSrc={cmsMediaUrl(previewUrl)}
                  fullSrc={cmsMediaUrl(fullUrl)}
                  posterSrc={cmsMediaUrl(posterUrl)}
                  handle={item.instagramHandle ?? ""}
                  followers={item.instagramFollowers ?? ""}
                  caption={item.quote}
                  cmsId={item.id}
                  editLocale={editLocale}
                />
              </div>
            )
          }
          // type === "video"
          const previewUrl = item.previewVideo?.url
          const fullUrl = item.fullVideo?.url
          const posterUrl = item.posterImage?.url
          if (!previewUrl || !fullUrl || !posterUrl) return null
          return (
            <div key={item.id} className="w-[55vw] flex-shrink-0 md:w-[300px]">
              <VideoTestimonial
                previewSrc={cmsMediaUrl(previewUrl)}
                fullSrc={cmsMediaUrl(fullUrl)}
                posterSrc={cmsMediaUrl(posterUrl)}
                title={item.authorName}
                subtitle={item.authorTitle ?? ""}
                aspectClass={videoAspectClass}
                cmsId={item.id}
                editLocale={editLocale}
              />
            </div>
          )
        })}
      </div>

      {resolvedViewAllHref ? (
        <div className="mx-auto mt-4 flex max-w-[1440px] items-center justify-between gap-3 px-4 md:px-10">
          <Link
            href={resolvedViewAllHref}
            onClick={() =>
              track.ctaClick(
                "All reviews",
                "testimonials-carousel",
                resolvedViewAllHref
              )
            }
            className={cn(buttonVariants({ variant: "outline" }))}
          >
            {resolvedViewAllLabel}
          </Link>
          <CarouselNav
            onPrev={() => navigate("left")}
            onNext={() => navigate("right")}
            atStart={atStart}
            atEnd={atEnd}
          />
        </div>
      ) : (
        <CarouselNav
          onPrev={() => navigate("left")}
          onNext={() => navigate("right")}
          atStart={atStart}
          atEnd={atEnd}
          className="mx-auto mt-4 max-w-[1440px] px-4 md:px-10"
        />
      )}
      {/* Position announcement for screen readers — only updated when the
          user presses prev/next so casual scrolling stays silent. */}
      <div role="status" aria-live="polite" className="sr-only">
        {announcement}
      </div>
    </section>
  )
}
